'use client';

/**
 * HubActions — Profileia Hub quick actions section (SPEC-101 Task E / P-142).
 *
 * Spec ref:
 *   SPEC-101 §1 (P-142 — Profileia Hub rediseño Opción C Founder-approved),
 *   SPEC-095 (Profileia Hub original — CopyLinkButton, VCardDownloadButton,
 *             PublicProfileQR).
 *   SPEC-119 Task E (Analytics tracking — click tracking on vCard/share/
 *                    QR/market buttons).
 *
 * Renders the "ACCIONES RÁPIDAS" section of the Profileia Hub `/{username}`:
 *   - Descargar vCard (button — VCardDownloadButton client component)
 *   - Compartir perfil (anchor — WhatsApp share intent URL)
 *   - Copiar enlace (button — CopyLinkButton client component)
 *   - QR code (collapsible — PublicProfileQR client component)
 *
 * This is a CLIENT component because:
 *   - VCardDownloadButton uses the Blob API (browser-only).
 *   - CopyLinkButton uses navigator.clipboard.writeText (browser-only).
 *   - PublicProfileQR renders a canvas (browser-only).
 *   - SPEC-119 Task E: the click tracking uses `fetch` (browser-only).
 *
 * §6 Anti-Invención: pure UI. The component receives the public profile
 * data as props + renders the 4 actions. NO data fetching, NO business
 * logic.
 *
 * Anti-P-083: NO internal codenames in user-facing strings — labels are
 * plain Spanish ("Descargar vCard", "Compartir perfil", "Copiar enlace",
 * "Ver código QR", "Ocultar código QR").
 *
 * PR #1439 — Founder directive 2026-10-19: "haz que el QR de nuevo sea
 * desplegable y redireccione a la tarjeta digital y no a guardar contacto".
 * Two changes:
 *   1. QR is now COLLAPSIBLE (toggle button "Ver código QR" / "Ocultar
 *      código QR"). Pre-fix (PR #712) it was always visible. The founder
 *      wants it hidden by default + expandable on click.
 *   2. QR now contains the PROFILE URL (not vCard data). Pre-fix (PR #745)
 *      it contained vCard 3.0 data so scanning offered to save the contact.
 *      The founder wants scanning to REDIRECT to the tarjeta digital (the
 *      public profile page) instead. This is better UX — the visitor sees
 *      the full digital card with all CTAs, social links, etc. rather than
 *      just a contact entry.
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { VCardDownloadButton } from '@/components/profile/vcard-download-button';
import { CopyLinkButton } from '@/components/profile/copy-link-button';
import { PublicProfileQR } from '@/components/profile/public-qr';
import { QrCode, ChevronDown, ChevronUp } from 'lucide-react';

export interface HubActionsProps {
  /** The canonical profile URL — used for Share + Copy + QR. */
  profileUrl: string;
  /** The public profile object — passed to VCardDownloadButton as-is. */
  vcardProfile: {
    name: string;
    username: string;
    bio: string;
    avatar: string;
    location: string;
    packageTier: string;
    packageName: string;
    referralCode: string;
    social: Record<string, string>;
  };
}

// SPEC-119 Task E (Analytics tracking — click tracking): fire-and-forget
// POST to `/api/public/:username/click` with `{ target }` body. Errors are
// swallowed silently — analytics is best-effort + must NEVER break the UX
// (the click handler's primary action — vCard download, share, QR, etc —
// runs BEFORE the tracking call so the visitor's UX is unaffected even
// if the analytics endpoint is unreachable).
//
// The helper is module-scoped (not a React hook) because it has no state —
// it's a pure side-effect function. We pull `username` from
// `vcardProfile.username` so the caller doesn't have to pass it separately.
function trackClick(username: string, target: 'vcard' | 'share' | 'qr' | 'market' | 'social'): void {
  if (typeof window === 'undefined') return;
  fetch(`/api/public/${encodeURIComponent(username)}/click`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target }),
  }).catch(() => {
    // Intentionally swallow — analytics must NEVER break the UX.
  });
}

export function HubActions({ profileUrl, vcardProfile }: HubActionsProps) {
  // SPEC-119 Task E: cache the username for the click tracking helper so
  // we don't have to read `vcardProfile.username` on every click.
  const username = vcardProfile.username;

  // PR #1439 — QR is now collapsible (hidden by default, expanded on click).
  // Pre-fix (PR #712) it was always visible. The founder wants it
  // "desplegable" (expandable/collapsible) again.
  const [qrExpanded, setQrExpanded] = React.useState(false);

  return (
    <section
      aria-labelledby="hub-actions-heading"
      className="mt-8 pt-6 border-t border-slate-700"
    >
      <h2
        id="hub-actions-heading"
        className="text-sm font-semibold text-white inline-flex items-center gap-2 mb-3"
      >
        Acciones rápidas
      </h2>

      {/* Action buttons — flex-wrap so they stack on mobile (1 col) + sit
          side-by-side on desktop (3 cols). */}
      <div className="flex flex-wrap justify-center gap-3">
 {/* Task E: wrap the vCard download button in a div so we
            can attach the click tracking handler without modifying the
            shared VCardDownloadButton component (which is also used by
            /p/[username] and other consumers — modifying its API would
            ripple. The wrapping div catches the click via event bubbling). */}
        <div
          onClick={() => trackClick(username, 'vcard')}
          className="contents"
        >
          <VCardDownloadButton
            profile={vcardProfile}
            className="px-4 py-2 text-sm rounded-lg bg-[#2563EB] text-white hover:bg-[#2563EB]/90 transition"
          />
        </div>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`Mi perfil en Profileia: ${profileUrl}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick(username, 'share')}
          className="px-4 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition inline-flex items-center gap-1.5"
        >
          Compartir
        </a>
        <CopyLinkButton
          url={profileUrl}
          className="px-4 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition"
        />
      </div>

      {/* PR #1439 — QR collapsible (toggle button). Pre-fix (PR #712) it was
          always visible. Founder directive 2026-10-19: "haz que el QR de
          nuevo sea desplegable". Hidden by default, expanded on click. */}
      <div className="flex justify-center mt-4">
        <button
          type="button"
          onClick={() => {
            setQrExpanded((prev) => !prev);
            trackClick(username, 'qr');
          }}
          aria-expanded={qrExpanded}
          aria-controls="hub-qr-code"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition"
        >
          <QrCode className="size-4" aria-hidden="true" />
          {qrExpanded ? 'Ocultar código QR' : 'Ver código QR'}
          {qrExpanded ? <ChevronUp className="size-3.5" aria-hidden="true" /> : <ChevronDown className="size-3.5" aria-hidden="true" />}
        </button>
      </div>

      {qrExpanded && (
        <div id="hub-qr-code" className="flex flex-col items-center gap-2 mt-4">
          {/* PR #1439 — QR now contains the PROFILE URL (not vCard data).
              Pre-fix (PR #745) it contained vCard 3.0 data so scanning
              offered to save the contact. Founder directive: scanning
              should REDIRECT to the tarjeta digital (the public profile
              page) so the visitor sees the full digital card with all
              CTAs, social links, etc. */}
          <PublicProfileQR value={profileUrl} size={256} />
          <p className="text-xs text-slate-400">
            Escanea para abrir mi tarjeta digital
          </p>
        </div>
      )}
    </section>
  );
}
